import { useCallback, useState } from 'react'

import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router'
import { Select } from 'src/components/Select/Select'
import { WarningMessage } from 'src/components/WarningMessage/WarningMessage'
import { useAuthenticatedAccount } from 'src/features/auth/context'
import { useVideoCallRoom } from 'src/features/videoCall/context/videoCallRoomContext'

import { useFinishExplanationSession } from '../../../explanation/api/finishSession'
import { useSessionForFinishSession } from '../../api/getSessionForFinishSession'
import {
  useActiveDevice,
  useUpdateRevisionStatus,
} from '../../context/ExplanationRoomEvent'
import { useThisSession } from '../../hooks/useThisSession'
import { isAgreementCompletedOrRejected } from '../../utils/explanationRevisionStatusUtils'
import { getRoomFinishedRoute } from '../../utils/getRoomFinishedRoute'

type Props = {
  sessionUid: string
}

export const FinishSession: React.FC<Props> = ({ sessionUid }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  const thisSession = useThisSession({ sessionUid, forPatient: false })

  const { data: session, mutate: mutateSession } = useSessionForFinishSession({
    explanationSessionUid: sessionUid,
  })

  // 説明終了方法 全員終了 or 自分のみ退出
  const [finishSessionMethod, setFinishSessionMethod] = useState<
    'FinishAll' | 'OnlyMe' | undefined
  >(undefined)

  useUpdateRevisionStatus({
    listener: async () => {
      await mutateSession()
    },
  })

  const {
    account: { selectedTrialHospitalUid },
  } = useAuthenticatedAccount()

  const isPartnerMember =
    session?.explanationRevision.partnerTrialHospital?.uid ===
    selectedTrialHospitalUid

  const { request: finishSession } = useFinishExplanationSession({
    onSuccess: () => {
      if (thisSession.fetched) {
        thisSession.mutate()
      }
    },
  })

  const navigate = useNavigate()

  const { sendDeleted } = useActiveDevice()

  const { leave: leaveVideoCallRoom } = useVideoCallRoom()

  const leaveRoom = useCallback(async () => {
    leaveVideoCallRoom()
    await sendDeleted()
    navigate(
      getRoomFinishedRoute({
        type: session?.explanationRevision.explanationType,
        isLeft: true,
        isPartnerMember,
      }),
    )
  }, [
    leaveVideoCallRoom,
    sendDeleted,
    navigate,
    session?.explanationRevision.explanationType,
    isPartnerMember,
  ])

  if (!session) {
    return null
  }

  const expType = session.explanationRevision.explanationType

  const browsedDocRevs = session.explanationRevision.docSets
    .flatMap(docRev => docRev.documentRevisions)
    .filter(docRev => !!docRev?.latestPatientSideBrowsingHistory)

  const status = session.explanationRevision.latestHistory.statusV2
  const isCompleted = isAgreementCompletedOrRejected(status)

  const canFinish = !isPartnerMember || isCompleted

  // 同意完了かつ未交付の場合はチェックを要求する
  // 一つも閲覧していない場合は交付不可なのでチェック不要
  const isAgreementDoneButNotDelivered =
    status === 'AgreementCompleted' && browsedDocRevs.length > 0

  const handleOpen = () => {
    // モーダルを開いた時点のsessionの状態に応じてmethodを設定
    if (canFinish) {
      setFinishSessionMethod(expType === 'InPerson' ? 'FinishAll' : undefined)
    }
    onOpen()
  }

  return (
    <>
      <Button onClick={handleOpen} variant="outline" colorScheme="red">
        {canFinish ? '面談を終了する' : '面談から退出する'}
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        {canFinish ? (
          <ModalContent>
            <ModalCloseButton />
            <ModalHeader>
              {isCompleted ? '面談を終了しますか？' : '面談から退出しますか？'}
            </ModalHeader>
            <ModalBody>
              <Stack spacing="2">
                {/* 対面の場合は操作ユーザーのみなので問答無用で終了（FinishAll） */}
                {expType !== 'InPerson' && (
                  <Stack spacing="3">
                    <Text>終了方法が選択できます。</Text>
                    <Select
                      value={finishSessionMethod}
                      items={[
                        {
                          value: 'OnlyMe',
                          label:
                            '自分だけが面談から退出する。※面談は継続されます。',
                        },
                        {
                          value: 'FinishAll',
                          label:
                            '面談自体を終了し、全参加者の説明画面を自動で閉じる。',
                        },
                      ]}
                      onChange={setFinishSessionMethod}
                    />
                  </Stack>
                )}
                {isAgreementDoneButNotDelivered && (
                  <WarningMessage message="署名済文書の交付が完了していません。本当に終了しますか？" />
                )}
              </Stack>
            </ModalBody>

            <ModalFooter>
              <HStack spacing="4">
                <Button colorScheme="green" onClick={onClose}>
                  面談を継続する
                </Button>
                <Button
                  colorScheme="red"
                  isDisabled={!finishSessionMethod}
                  onClick={async () => {
                    if (finishSessionMethod === 'OnlyMe') {
                      await leaveRoom()
                      return
                    }
                    await finishSession({ explanationSessionUid: session.uid })
                  }}
                >
                  終了する
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        ) : (
          <ModalContent>
            <ModalHeader>面談から退出する</ModalHeader>
            <ModalBody>
              {isAgreementCompletedOrRejected(status) ? (
                <Text>面談から退出しますか？</Text>
              ) : (
                <Text>同意は完了していません。面談から退出しますか？</Text>
              )}
            </ModalBody>

            <ModalFooter>
              <Button colorScheme="green" onClick={onClose}>
                面談を継続する
              </Button>
              <Button colorScheme="red" onClick={leaveRoom}>
                退出する
              </Button>
            </ModalFooter>
          </ModalContent>
        )}
      </Modal>
    </>
  )
}
